import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { formatPrice, parseSwatch } from "@/data/products";
import { useProducts } from "@/context/ProductsContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { Heart, Truck, Scissors, Ruler, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ProductCard from "@/components/ProductCard";
import ProductSEO from "@/components/ProductSEO";

export default function ProductDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";
  const { findProduct, products, loading } = useProducts();
  const { user } = useAuth();
  const [draft, setDraft] = useState(null);
  const product = draft || findProduct(slug);

  // If admin opens ?preview=1 and the piece is a draft, fetch it directly
  useEffect(() => {
    if (!isPreview || user?.role !== "admin") return;
    if (findProduct(slug)) return; // already visible via public catalog
    api.get(`/admin/products/slug/${slug}`).then(r => setDraft(r.data)).catch(() => {});
  }, [isPreview, user, slug, findProduct]);
  const nav = useNavigate();
  const { add, setOpen: openCart } = useCart();
  const { has, toggle } = useWishlist();
  const [size, setSize] = useState(product?.sizes?.[0] || "");
  const [color, setColor] = useState(product?.colors?.[0] || "");
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [sizeGuide, setSizeGuide] = useState(false);
  const [unit, setUnit] = useState("in");

  if (loading) return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="product-detail-loading">
      <div className="container-lux grid md:grid-cols-2 gap-10 lg:gap-16">
        <div className="aspect-[3/4] bg-muted animate-pulse" />
        <div className="space-y-5 py-8">
          <div className="h-3 w-24 bg-muted animate-pulse" /><div className="h-12 w-3/4 bg-muted animate-pulse" /><div className="h-4 w-1/2 bg-muted animate-pulse" /><div className="h-8 w-32 bg-muted animate-pulse" />
        </div>
      </div>
    </div>
  );
  if (!product) return (
    <div className="pt-32 container-lux min-h-[60vh]"><h1 className="font-serif text-4xl">Piece not found</h1><Link to="/collections" className="lux-link mt-6 inline-block">Back to collections</Link></div>
  );

  const you = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);
  const addToBag = () => { add(product, size, color, qty); };
  const buyNow = () => { add(product, size, color, qty); nav("/checkout"); };

  return (
    <div className="pt-28 pb-24 bg-ivory" data-testid="product-detail">
      <ProductSEO product={product} />
      {isPreview && user?.role === "admin" && (
        <div className="bg-ink text-ivory text-center py-3 -mt-28 mb-10 sticky top-0 z-40">
          <p className="overline">Preview mode · {product.is_active === false ? "Draft (hidden from customers)" : "Published"} · <Link to="/admin/products" className="underline">Return to CMS</Link></p>
        </div>
      )}
      <div className="container-lux">
        <nav className="overline text-muted-foreground mb-8" aria-label="Breadcrumb">
          <Link to="/">Home</Link> · <Link to="/collections">Collections</Link> · <span className="text-ink">{product.title}</span>
        </nav>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 min-w-0">
          <div>
            <div className="relative aspect-[3/4] overflow-hidden bg-muted group">
              <img src={product.images[active]} alt={product.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
            </div>
            <div className="product-gallery-grid grid grid-cols-4 gap-3 mt-3 min-w-0">
              {product.images.map((im, i) => (
                <button key={i} onClick={() => setActive(i)} className={`aspect-[3/4] overflow-hidden border ${active===i?"border-ink":"border-transparent"}`} data-testid={`thumb-${i}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="product-detail-content md:sticky md:top-28 self-start min-w-0">
            <p className="overline text-muted-foreground">{product.tag || "Couture"}</p>
            <h1 className="mt-3 font-serif tracking-tighter text-4xl md:text-6xl font-light leading-[0.95]">{product.title}</h1>
            <p className="mt-3 text-sm text-muted-foreground">{product.subtitle}</p>
            <p className="mt-6 font-serif text-2xl" data-testid="detail-price">{formatPrice(product.price)}</p>
            <p className="text-xs text-muted-foreground mt-1">Inclusive of all taxes · Made to order</p>

            <div className="mt-10">
              <p className="overline">Colour · <span className="text-muted-foreground">{color ? (parseSwatch(color).name || parseSwatch(color).hex) : ""}</span></p>
              <div className="flex gap-3 mt-4">
                {product.colors.map(c => {
                  const p = parseSwatch(c);
                  const key = c;
                  const label = p.name && p.name !== p.hex ? p.name : p.hex;
                  return (
                    <button key={key} onClick={() => setColor(c)} data-testid={`color-${key}`}
                      className={`w-9 h-9 border ${color===c?"border-ink":"border-border"} flex items-center justify-center`}
                      title={label} aria-label={label}
                    ><span className="w-6 h-6 rounded-full block border border-black/10" style={{ background: p.hex }} /></button>
                  );
                })}
              </div>
            </div>

            <div className="mt-8">
              <div className="flex justify-between items-baseline">
                <p className="overline">Size</p>
                <button onClick={() => setSizeGuide(true)} className="overline text-muted-foreground hover:text-ink" data-testid="open-size-guide">Size Guide →</button>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {product.sizes.map(s => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`px-5 py-3 text-xs uppercase tracking-widest border ${size===s?"bg-ink text-ivory border-ink":"border-border hover:border-ink"}`}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <p className="overline">Quantity</p>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center border border-border">
                  <button onClick={() => setQty(Math.max(1, qty-1))} className="px-4 py-3">−</button>
                  <span className="px-4 min-w-[3rem] text-center" data-testid="detail-qty">{qty}</span>
                  <button onClick={() => setQty(qty+1)} className="px-4 py-3">+</button>
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <button data-testid="add-to-cart-btn" onClick={addToBag} className="lux-btn-dark flex-1">Add To Bag</button>
              <button data-testid="buy-now-btn" onClick={buyNow} className="lux-btn-outline flex-1">Buy Now</button>
              <button data-testid="wishlist-detail" onClick={() => toggle(product)} className={`w-14 h-14 border flex items-center justify-center ${has(product.id)?"bg-ink text-ivory border-ink":"border-ink"}`}><Heart size={16} strokeWidth={1} /></button>
            </div>

            {(() => {
              const waNumber = "919667245584";
              const lines = [
                `Hello BARIQUE Atelier,`,
                ``,
                `I would love to know more about this piece:`,
                `• ${product.title}${product.subtitle ? ` — ${product.subtitle}` : ""}`,
                `• Price: ${formatPrice(product.price)}`,
                size ? `• Size: ${size}` : null,
                color ? `• Colour: ${color}` : null,
                qty > 1 ? `• Quantity: ${qty}` : null,
                `• Link: ${typeof window !== "undefined" ? window.location.origin : ""}/product/${product.slug}`,
                ``,
                `Could you please share fabric details, fitting and delivery timeline?`,
                `Thank you.`,
              ].filter(Boolean).join("\n");
              const href = `https://wa.me/${waNumber}?text=${encodeURIComponent(lines)}`;
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="whatsapp-enquire"
                  className="mt-4 flex items-center justify-center gap-3 w-full py-4 border border-[#25D366] text-[#128C7E] uppercase tracking-[0.24em] text-[11px] font-medium hover:bg-[#25D366] hover:text-ivory transition-colors duration-500"
                >
                  <MessageCircle size={16} strokeWidth={1.4} />
                  Enquire On WhatsApp
                </a>
              );
            })()}

            <div className="mt-10 space-y-3 text-xs text-muted-foreground">
              <p className="flex items-center gap-2"><Scissors size={14} strokeWidth={1} /> Handcrafted in Delhi over {product.delivery}</p>
              <p className="flex items-center gap-2"><Truck size={14} strokeWidth={1} /> Complimentary worldwide shipping</p>
              <p className="flex items-center gap-2"><Ruler size={14} strokeWidth={1} /> Complimentary bespoke fitting</p>
            </div>

            <div className="mt-12">
              <Tabs defaultValue="desc">
                <TabsList className="w-full justify-start rounded-none bg-transparent border-b border-border p-0 h-auto">
                  {[["desc","Description"],["craft","Components"],["ship","Shipping"],["care","Care"]].map(([v,l])=>(
                    <TabsTrigger key={v} value={v} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-ink data-[state=active]:shadow-none data-[state=active]:bg-transparent px-0 mr-8 pb-3 text-xs uppercase tracking-widest">{l}</TabsTrigger>
                  ))}
                </TabsList>
                <TabsContent value="desc" className="pt-6 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {product.description || `${product.subtitle}. A signature Barique creation reimagined for the modern woman. Cut to a sculpted silhouette, finished by the atelier over hundreds of hours.`}
                </TabsContent>
                <TabsContent value="craft" className="pt-6 text-sm text-muted-foreground leading-relaxed">
                  <p><strong className="text-ink">Fabric:</strong> {product.fabric}</p>
                  <p className="mt-3"><strong className="text-ink">Components:</strong> {product.craftsmanship}</p>
                </TabsContent>
                <TabsContent value="ship" className="pt-6 text-sm text-muted-foreground leading-relaxed">
                  Complimentary worldwide shipping. Estimated delivery in {product.delivery}. Bespoke pieces are non-returnable.
                </TabsContent>
                <TabsContent value="care" className="pt-6 text-sm text-muted-foreground leading-relaxed">
                  Dry clean only. Store flat, wrapped in muslin. Keep away from direct sunlight and perfume.
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>

        {you.length > 0 && (
          <div className="mt-32">
            <h2 className="font-serif tracking-tighter text-3xl md:text-5xl font-light">You May Also Love</h2>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {you.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>

      <Dialog open={sizeGuide} onOpenChange={setSizeGuide}>
        <DialogContent className="max-w-2xl bg-ivory border-border rounded-none">
          <DialogHeader><DialogTitle className="font-serif font-light text-3xl tracking-tighter">Size Guide</DialogTitle></DialogHeader>
          <div className="flex justify-end gap-2 mb-4">
            <button onClick={() => setUnit("in")} data-testid="unit-in" className={`overline px-3 py-1 ${unit==="in"?"bg-ink text-ivory":""}`}>Inches</button>
            <button onClick={() => setUnit("cm")} data-testid="unit-cm" className={`overline px-3 py-1 ${unit==="cm"?"bg-ink text-ivory":""}`}>Cm</button>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left overline text-muted-foreground">
                <th className="py-3">Size</th><th>Chest</th><th>Waist</th><th>Hip</th><th>Shoulder</th>
              </tr>
            </thead>
            <tbody>
              {[["XS",32,26,34,14],["S",34,28,36,14.5],["M",36,30,38,15],["L",38,32,40,15.5],["XL",40,34,42,16]].map(r => (
                <tr key={r[0]} className="border-b border-border/60">
                  <td className="py-3">{r[0]}</td>
                  {r.slice(1).map((v,i) => <td key={i}>{unit==="cm" ? (v*2.54).toFixed(1) : v}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-4">Measurements refer to body — not the garment.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const swatch = (name) => parseSwatch(name).hex;
